import { Button } from "@/components/ui/button";
import { Pen, Type, Calendar, Stamp, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface EditorToolbarProps {
  currentPage: number;
  numPages: number;
  zoomLevel: number;
  onPageChange: (page: number) => void;
  onAddSignature: () => void;
  onAddText: () => void;
  onAddDate: () => void;
  onAddStamp: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
}

export function EditorToolbar({
  currentPage,
  numPages,
  zoomLevel,
  onPageChange,
  onAddSignature,
  onAddText,
  onAddDate,
  onAddStamp,
  onZoomIn,
  onZoomOut,
  onResetZoom,
}: EditorToolbarProps) {
  const isMobile = useIsMobile();

  // Mobile bottom bar
  if (isMobile) {
    return (
      <>
        {/* Top navigation bar */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm min-w-[50px] text-center">
              {currentPage}/{numPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange(Math.min(numPages, currentPage + 1))}
              disabled={currentPage >= numPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="flex items-center gap-1">
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={onZoomOut}>
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-xs min-w-[40px] text-center tabular-nums">
              {Math.round(zoomLevel * 100)}%
            </span>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={onZoomIn}>
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Bottom tool bar - fixed */}
        <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-2 z-50 safe-area-pb">
          <div className="flex justify-around max-w-md mx-auto">
            <ToolButton icon={Pen} label="Signature" onClick={onAddSignature} />
            <ToolButton icon={Type} label="Texte" onClick={onAddText} />
            <ToolButton icon={Calendar} label="Date" onClick={onAddDate} />
            <ToolButton icon={Stamp} label="Tampon" onClick={onAddStamp} />
          </div>
        </div>
      </>
    );
  }

  // Desktop toolbar
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
      {/* Left: Tool buttons - All equal, no mode toggle */}
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" onClick={onAddSignature} className="gap-1.5">
          <Pen className="w-4 h-4" />
          Signature
        </Button>
        <Button variant="outline" size="sm" onClick={onAddText} className="gap-1.5">
          <Type className="w-4 h-4" />
          Texte
        </Button>
        <Button variant="outline" size="sm" onClick={onAddDate} className="gap-1.5">
          <Calendar className="w-4 h-4" />
          Date
        </Button>
        <Button variant="outline" size="sm" onClick={onAddStamp} className="gap-1.5">
          <Stamp className="w-4 h-4" />
          Tampon
        </Button>
      </div>

      {/* Center: Page navigation */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm min-w-[60px] text-center">
          {currentPage} / {numPages}
        </span>
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(Math.min(numPages, currentPage + 1))}
          disabled={currentPage >= numPages}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Right: Zoom controls - always visible */}
      <div className="flex items-center gap-1">
        <Button size="icon" variant="outline" onClick={onZoomOut}>
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-sm min-w-[50px] text-center tabular-nums">
          {Math.round(zoomLevel * 100)}%
        </span>
        <Button size="icon" variant="outline" onClick={onZoomIn}>
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="outline" onClick={onResetZoom} title="Ajuster à la largeur">
          <RotateCcw className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

interface ToolButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
}

function ToolButton({ icon: Icon, label, onClick }: ToolButtonProps) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1 p-2 rounded-lg transition-colors min-w-[60px] hover:bg-secondary active:bg-secondary/80"
    >
      <Icon className="w-5 h-5" />
      <span className="text-xs">{label}</span>
    </button>
  );
}
